/**
 * Mensaje de error que devolvió una edge function.
 *
 * `supabase.functions.invoke` responde con un `FunctionsHttpError` cuyo `message` es siempre el
 * genérico "Edge Function returned a non-2xx status code"; el mensaje real que escribió la función
 * (`{ error: "…" }`) viene en el body de la respuesta, en `err.context` (un `Response`). Devuelve
 * ese mensaje, o '' si no se puede leer (el llamador cae a su mensaje genérico).
 */
export async function leerErrorDeEdgeFunction(err: unknown): Promise<string> {
  const ctx = (err as { context?: unknown } | null | undefined)?.context as
    | { clone?: () => Response; json?: () => Promise<unknown>; text?: () => Promise<string> }
    | undefined;
  if (!ctx || typeof ctx !== "object") return "";
  try {
    const res = typeof ctx.clone === "function" ? ctx.clone() : ctx;
    if (typeof res.json === "function") {
      const body = (await res.json()) as { error?: unknown; message?: unknown } | null;
      const msg = body?.error || body?.message;
      if (msg) return String(msg);
    }
  } catch {
    // Body que no es JSON (o ya consumido): se intenta como texto abajo.
  }
  try {
    if (typeof ctx.text === "function") {
      const txt = await ctx.text();
      if (txt) return txt.slice(0, 500);
    }
  } catch {
    // Sin body legible.
  }
  return "";
}

/**
 * `codigo` que escribió la edge en el body del error (p. ej. `{ error, codigo: "firma_en_curso" }`), para
 * que la pantalla reaccione a un caso concreto sin depender del texto. null si no viene o no se puede leer.
 */
export async function leerCodigoDeEdgeFunction(err: unknown): Promise<string | null> {
  const ctx = (err as { context?: { clone?: () => Response } } | null | undefined)?.context;
  if (!ctx || typeof ctx.clone !== "function") return null;
  try {
    const body = (await ctx.clone().json()) as { codigo?: unknown } | null;
    return typeof body?.codigo === "string" ? body.codigo : null;
  } catch {
    return null;
  }
}

